<template>
  <BaseLayout>
    <div class="space-y-6">
      <!-- Welcome Section -->
      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <UserGroupIcon class="h-8 w-8 text-primary-600" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">
                  Bienvenido al Panel de Administración
                </dt>
                <dd class="text-lg font-medium text-gray-900">
                  {{ authStore.user?.username }}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Grid -->
      <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <div
          v-for="stat in stats"
          :key="stat.name"
          class="bg-white overflow-hidden shadow rounded-lg animate-fade-in"
        >
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <component :is="stat.icon" class="h-6 w-6 text-gray-400" />
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">
                    {{ stat.name }}
                  </dt>
                  <dd class="text-lg font-medium text-gray-900">
                    <span v-if="statsLoading" class="animate-pulse">---</span>
                    <span v-else>{{ stat.value }}</span>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-5 py-3">
            <div class="text-sm">
              <router-link :to="stat.link" class="font-medium text-primary-600 hover:text-primary-500">
                Ver detalles
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="bg-white shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
            Acciones Rápidas
          </h3>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <router-link
              v-for="action in quickActions"
              :key="action.name"
              :to="action.href"
              class="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500 rounded-lg border border-gray-200 hover:border-primary-300 transition-colors"
            >
              <div>
                <span class="rounded-lg inline-flex p-3 ring-4 ring-white" :class="action.iconBackground">
                  <component :is="action.icon" class="h-6 w-6" :class="action.iconForeground" />
                </span>
              </div>
              <div class="mt-4">
                <h3 class="text-lg font-medium text-gray-900">
                  {{ action.name }}
                </h3>
                <p class="mt-2 text-sm text-gray-500">
                  {{ action.description }}
                </p>
              </div>
            </router-link>
          </div>
        </div>
      </div>

      <!-- Recent Activity -->
      <div class="bg-white shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
            Estado de Servicios
          </h3>
          <div class="space-y-4">
            <div
              v-for="service in services"
              :key="service.name"
              class="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div class="flex items-center">
                <div class="flex-shrink-0">
                  <div class="h-3 w-3 rounded-full" :class="service.status === 'online' ? 'bg-green-400' : 'bg-red-400'"></div>
                </div>
                <div class="ml-3">
                  <p class="text-sm font-medium text-gray-900">{{ service.name }}</p>
                  <p class="text-sm text-gray-500">{{ service.description }}</p>
                </div>
              </div>
              <div class="flex items-center">
                <span class="text-sm" :class="service.status === 'online' ? 'text-green-600' : 'text-red-600'">
                  {{ service.status === 'online' ? 'En línea' : 'Desconectado' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseLayout>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { apiService } from '@/services/apiService'
import BaseLayout from '@/components/BaseLayout.vue'
import {
  UserGroupIcon,
  UsersIcon,
  PlusIcon,
  DocumentArrowUpIcon,
  ChartBarIcon,
  CogIcon
} from '@heroicons/vue/24/outline'

const authStore = useAuthStore()

const statsLoading = ref(true)
const stats = ref([
  {
    name: 'Total Usuarios',
    value: '0',
    icon: UsersIcon,
    link: '/users'
  },
  {
    name: 'Usuarios Activos',
    value: '0',
    icon: UserGroupIcon,
    link: '/users'
  },
  {
    name: 'Validadores',
    value: '0',
    icon: UsersIcon,
    link: '/users'
  },
  {
    name: 'Digitadores',
    value: '0',
    icon: UsersIcon,
    link: '/users'
  },
  {
    name: 'Supervisores',
    value: '0',
    icon: UsersIcon,
    link: '/users'
  },
  {
    name: 'Transmisores',
    value: '0',
    icon: UsersIcon,
    link: '/users'
  },
  {
    name: 'Auditores',
    value: '0',
    icon: UsersIcon,
    link: '/users'
  }
])

const quickActions = [
  {
    name: 'Crear Usuario',
    description: 'Agregar un nuevo usuario al sistema',
    href: '/users/create',
    icon: PlusIcon,
    iconForeground: 'text-primary-600',
    iconBackground: 'bg-primary-100'
  },
  {
    name: 'Importar Usuarios',
    description: 'Importar usuarios desde archivo CSV',
    href: '/users/import',
    icon: DocumentArrowUpIcon,
    iconForeground: 'text-green-600',
    iconBackground: 'bg-green-100'
  },
  {
    name: 'Ver Estadísticas',
    description: 'Revisar estadísticas del sistema',
    href: '/statistics',
    icon: ChartBarIcon,
    iconForeground: 'text-blue-600',
    iconBackground: 'bg-blue-100'
  },
  {
    name: 'Gestión Keycloak',
    description: 'Administrar usuarios y roles en Keycloak',
    href: '/keycloak',
    icon: CogIcon,
    iconForeground: 'text-purple-600',
    iconBackground: 'bg-purple-100'
  }
]

const services = ref([
  {
    name: 'Backend de Validación',
    description: 'Servicio de validación de actas',
    status: 'offline'
  },
  {
    name: 'Backend de Digitación',
    description: 'Servicio de digitación',
    status: 'offline'
  },
  {
    name: 'Backend de Transmisión',
    description: 'Servicio de transmisión',
    status: 'offline'
  },
  {
    name: 'Keycloak',
    description: 'Servidor de autenticación',
    status: 'offline'
  }
])

async function loadStats() {
  try {
    statsLoading.value = true
    
    // Cargar estadísticas de usuarios
    const userStats = await apiService.getUserStats() as any
    
    stats.value[0].value = userStats.total.toString()
    stats.value[1].value = userStats.totalEnabled.toString()
    
    // Buscar estadísticas por rol
    const validadores = userStats.byRole.find((r: any) => r._id === 'VALIDACION')
    const digitadores = userStats.byRole.find((r: any) => r._id === 'DIGITACION')
    const supervisores = userStats.byRole.find((r: any) => r._id === 'SUPERVISION')
    const transmisores = userStats.byRole.find((r: any) => r._id === 'TRANSMISION')
    const auditores = userStats.byRole.find((r: any) => r._id === 'AUDITOR')
    
    stats.value[2].value = validadores?.count?.toString() || '0'
    stats.value[3].value = digitadores?.count?.toString() || '0'
    stats.value[4].value = supervisores?.count?.toString() || '0'
    stats.value[5].value = transmisores?.count?.toString() || '0'
    stats.value[6].value = auditores?.count?.toString() || '0'
    
    // Cargar estado de servicios
    const systemStats = await apiService.getStatistics() as any
    
    if (systemStats.services) {
      services.value[0].status = systemStats.services.validacion?.available ? 'online' : 'offline'
      services.value[1].status = systemStats.services.digitacion?.available ? 'online' : 'offline'
      services.value[2].status = systemStats.services.transmision?.available ? 'online' : 'offline'
    }
    
    // Keycloak siempre online si llegamos hasta aquí
    services.value[3].status = 'online'
    
  } catch (error) {
    console.error('Error cargando estadísticas:', error)
  } finally {
    statsLoading.value = false
  }
}

onMounted(() => {
  loadStats()
})
</script>
